import { useEffect, useRef } from "react";
import type { StandoutScoreData } from "@/lib/api";
import { StandoutScore } from "@/components/cars/StandoutScore";

/** Editorial-only accessibility bridge; the settled listing widget is unchanged. */
export function ApexScorePresentation({ score, onInlineBreakdown }: {
  score: StandoutScoreData;
  onInlineBreakdown: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let dialog: HTMLElement | null = null;
    const trigger = root.current?.querySelector("button");
    const focusables = () => [...(dialog?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], summary, [tabindex="0"]',
    ) || [])].filter(node => node.getClientRects().length);
    const observe = () => {
      const next = document.querySelector<HTMLElement>('.apex-score[role="dialog"]');
      if (next && next !== dialog) {
        dialog = next;
        focusables()[0]?.focus();
      } else if (!next && dialog) {
        dialog = null;
        trigger?.focus();
      }
    };
    const keyboard = (event: KeyboardEvent) => {
      if (!dialog || event.key !== "Tab") return;
      const items = focusables();
      const first = items[0], last = items.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    const observer = new MutationObserver(observe);
    observer.observe(document.body, { childList: true });
    document.addEventListener("keydown", keyboard, true);
    return () => { observer.disconnect(); document.removeEventListener("keydown", keyboard, true); };
  }, []);
  return <div ref={root} onClickCapture={event => {
    // Existing widget's JS ring animates. Reduced-motion readers use the exact
    // same published data in the native, non-animated breakdown below instead.
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    event.preventDefault();
    event.stopPropagation();
    onInlineBreakdown();
  }}>
    <StandoutScore score={score} detail />
  </div>;
}
